import {joiResolver} from '@hookform/resolvers/joi';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';

import {makeStyles} from 'tss-react/mui';
import Joi from 'joi';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {MutationStatus, UseMutateFunction} from 'react-query';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextFieldForm,
  Typography,
} from 'web-library';
import React from 'react';
import {
  PayNowProxyRequestBody
} from '../../../../api-refactored/exam-registration/usePostPayNowProxyDetails';
import {IExamMasterCodeItem} from '../../../../api/user/model';
import {
  getMasterCodeObjectDropdownSchema,
  getPhoneNumberSchema,
  validateNRIC,
  validateSingaporeCountry,
} from '../../../../utils/JoiUtils';
import {getIExamMasterCodeSelected} from '../../../profile/logic/useCandidateProfileForm';
import {ContactNumberFormItem} from '../../../../components/form-items/ContactNumberFormItem';

const useStyles = makeStyles()((theme) =>
    ({
      titleContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      warningContent: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
      },
      dialog: {
        overflow: 'hidden',
        maxHeight: 'unset !important',
      },
      buttonContainer: {
        float: 'right',
        marginBottom: theme.spacing(2),
      },
      confirmChangesDialogUpperContent: {
        marginBottom: theme.spacing(1),
      },
      confirmChangesDialogContent: {
        marginBottom: theme.spacing(3),
      },
      formElementContainer: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('lg')]: {
          marginBottom: theme.spacing(3),
        },
        [theme.breakpoints.down('md')]: {
          marginBottom: theme.spacing(2),
        },
        width: '100%',
      },
      radioContainer: {
        marginBottom: theme.spacing(4),
      },
      requiredLabel: {
        color: theme.color.state.stateImportant,
        marginLeft: theme.spacing(0.5),
        display: 'inline',
      },
      keyLabel: {
        color: theme.color.text.textHeader,
      },
    }));

interface PayNowProxyDetailsModalProps {
  modalState: boolean;
  setOpenModalState: React.Dispatch<React.SetStateAction<boolean>>;
  postPayNowProxyDetails: UseMutateFunction<any, any, PayNowProxyRequestBody, any>;
  applicationUuid: string;
  apiButtonStatus: MutationStatus;
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  onClosePayNowProxyModal?: () => void;
  // message to display on top of the modal content
  modalInstruction?: string;
}

const PayNowProxyDetailsModal = ({
                                   modalState,
                                   setOpenModalState,
                                   postPayNowProxyDetails,
                                   applicationUuid,
                                   apiButtonStatus,
                                   setApiButtonStatus,
                                   onClosePayNowProxyModal,
                                   modalInstruction
                                 }: PayNowProxyDetailsModalProps) => {
  const {classes} = useStyles();
  const {t} = useTranslation();

  const closeModal = () => {
    setOpenModalState(false);
    if (onClosePayNowProxyModal) {
      onClosePayNowProxyModal();
    }
  };

  interface PayNowProxyDetailSchema {
    name: string;
    mobileOrNric: string;
    mobileCountryCode: IExamMasterCodeItem;
    mobileContactNumber: string;
    confirmMobileCountryCode: IExamMasterCodeItem;
    confirmMobileContactNumber: string;
    nricFin: string;
    confirmNricFin: string;
  }

  const payNowProxyDetailValidationSchema = () =>
      Joi.object<PayNowProxyDetailSchema>({
        name: Joi.string().required().messages({
          'string.empty': 'Please enter name',
        }),
        mobileOrNric: Joi.string().allow(''),
        mobileCountryCode: Joi.when('mobileOrNric', {
          is: 'M',
          then: getMasterCodeObjectDropdownSchema(false, 'country code'),
          otherwise: Joi.any().allow(null),
        }),
        mobileContactNumber: Joi.when('mobileOrNric', {
          is: 'M',
          then: Joi.when('mobileCountryCode', {
            is: validateSingaporeCountry,
            then: getPhoneNumberSchema(false, 'Mobile No.', 8, 8),
            otherwise: getPhoneNumberSchema(false, 'Mobile No.', 15),
          }),
          otherwise: Joi.string().allow(''),
        }),
        confirmMobileCountryCode: Joi.when('mobileOrNric', {
          is: 'M',
          then: Joi.any().required().equal(Joi.ref('mobileCountryCode')).messages({
            'any.required': 'country code is not allowed to be empty',
            'object.based': 'country code is not allowed to be empty',
            'any.only': `Mobile Country Code do not match`,
          }),
          otherwise: Joi.any().allow(null),
        }),
        confirmMobileContactNumber: Joi.when('mobileOrNric', {
          is: 'M',
          then: Joi.string().required().valid(Joi.ref('mobileContactNumber')).messages({
            'string.based': `Re-enter mobile no. is not allowed to be empty`,
            'string.empty': `Re-enter mobile no. is not allowed to be empty`,
            'any.only': `Mobile No. do not match`,
          }),
          otherwise: Joi.string().allow(''),
        }),
        nricFin: Joi.when('mobileOrNric', {
          is: 'N',
          then: Joi.string()
          .alphanum()
          .required()
          .max(9)
          .custom((value, helpers) => validateNRIC(value, helpers), 'Validate NRIC/FIN/Passport No')
          .messages({
            'string.empty': `NRIC/FIN is not allowed to be empty.`,
            'string.alphanum': `NRIC / FIN should be alphanumeric.`,
            'string.max': 'NRIC / FIN is too long.',
            'string.pattern.nric': 'Invalid NRIC',
            'string.pattern.fin': 'Invalid FIN',
            'string.pattern.startCharError': 'Invalid NRIC / FIN',
            'string.pattern.nricYear':
                'Invalid NRIC format/NRIC format does not match with Year of Birth',
          }),
          otherwise: Joi.string().allow(''),
        }),
        confirmNricFin: Joi.when('mobileOrNric', {
          is: 'N',
          then: Joi.any().required().valid(Joi.ref('nricFin')).messages({
            'any.only': 'NRIC/FIN do not match',
            'any.required': 'confirm NRIC/FIN not allowed to be empty',
          }),
          otherwise: Joi.string().allow(''),
        }),
      });

  const formHook = useForm<PayNowProxyDetailSchema>({
    mode: 'onSubmit',
    resolver: joiResolver(payNowProxyDetailValidationSchema()),
  });

  const handleSubmitPayNowProxyDetails = (formData: any) => {
    setApiButtonStatus('loading');
    if (formData.mobileOrNric === 'M') {
      postPayNowProxyDetails({
        uuid: applicationUuid,
        payload: {
          name: formData.name,
          uinfin: undefined,
          confirmUinfin: undefined,
          mobileNumber: {
            countryCode: formData.mobileCountryCode,
            number: formData.mobileContactNumber,
          },
          confirmMobileNumber: {
            countryCode: formData.confirmMobileCountryCode,
            number: formData.confirmMobileContactNumber,
          },
        },
      });
    } else if (formData.mobileOrNric === 'N') {
      postPayNowProxyDetails({
        uuid: applicationUuid,
        payload: {
          name: formData.name,
          uinfin: formData.nricFin,
          confirmUinfin: formData.confirmNricFin,
          mobileNumber: undefined,
          confirmMobileNumber: undefined,
        },
      });
    }
  };

  const watchRadioButtonValue = formHook.watch('mobileOrNric', 'M');

  return (
      // <>
      // </>
      (<Dialog
          id="pay-now-proxy-details-dialog"
          fullWidth
          open={modalState}
          maxWidth="sm"
          slotProps={{
            paper: {
              className: classes.dialog
            }
          }}
      >
        <DialogTitle>
          <Box className={classes.titleContent}>
            <Typography variant="h4">{t('exam->>PayNowProxyDetails.ModalTitle')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box className={classes.warningContent}>
            {modalInstruction ? <Typography variant="body2" color="textPrimary">
                  {modalInstruction}
                </Typography> :
                <>
                  <Typography variant="body2" color="textPrimary">
                    {t('exam->>PayNowProxyDetails.ModalWarningLine1')}
                  </Typography>
                  <Typography variant="body2" color="textPrimary">
                    {t('exam->>PayNowProxyDetails.ModalWarningLine2')}
                  </Typography>
                </>
            }

          </Box>
          <form onSubmit={formHook.handleSubmit(handleSubmitPayNowProxyDetails)}>
            <Box className={classes.formElementContainer}>
              <TextFieldForm
                  form={formHook}
                  id="inputText-candidateProfile-name"
                  label={t('exam->>PayNowProxyDetails.NameLabel')}
                  error={!!formHook.formState.errors.name}
                  helperText={formHook.formState.errors?.name?.message?.toString()}
                  placeholder={t('exam->>PayNowProxyDetails.NamePlaceHolder')}
                  inputProps={{maxLength: 66}}
                  name="name"
                  type="text"
                  isRequired
                  fullWidth
              />
            </Box>
            <Box className={classes.radioContainer}>
              <InputLabel className={classes.keyLabel}>
                {t('exam->>PayNowProxyDetails.RadioButtonLabel')}
                <Typography className={classes.requiredLabel}>*</Typography>
              </InputLabel>
              <Controller
                  control={formHook.control}
                  defaultValue="M"
                  name="mobileOrNric"
                  render={({field}) => <RadioGroup
                      {...field}
                  >
                    <FormControlLabel
                        value="M"
                        name="mobileOrNric"
                        control={<Radio/>}
                        label={t('exam->>PayNowProxyDetails.MobileNumberLabel')}
                        key="form-control-label-select-payment-mode-mobile"
                    />
                    <FormControlLabel
                        value="N"
                        name="mobileOrNric"
                        control={<Radio/>}
                        label={t('exam->>PayNowProxyDetails.NricLabel')}
                        key="form-control-label-select-payment-mode-nric"
                    />

                  </RadioGroup>}/>
            </Box>
            {watchRadioButtonValue === 'M' && (
                <>
                  <Box className={classes.formElementContainer}>
                    <ContactNumberFormItem
                        id="payNowProxyMobileNumber"
                        formHook={formHook}
                        label={t('exam->>PayNowProxyDetails.MobileNumberLabel')}
                        placeholder={t('exam->>PayNowProxyDetails.MobileNumberPlaceHolder')}
                        countryCode={{
                          name: 'mobileCountryCode',
                          error: !!formHook.formState.errors.mobileCountryCode,
                          helperText: formHook.formState.errors?.mobileCountryCode?.message?.toString(),
                        }}
                        countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                        number={{
                          name: 'mobileContactNumber',
                          error: !!formHook.formState.errors.mobileContactNumber,
                          helperText: formHook.formState.errors?.mobileContactNumber?.message?.toString(),
                        }}
                        isPasteDisabled
                        isSkipTransform
                        isRequired
                        isDefaultToSingapore
                    />
                  </Box>
                  <Box className={classes.formElementContainer}>
                    <ContactNumberFormItem
                        id="payNowProxyConfirmMobileNumber"
                        formHook={formHook}
                        label={t('exam->>PayNowProxyDetails.ConfirmMobileNumberLabel')}
                        placeholder={t('exam->>PayNowProxyDetails.ConfirmMobileNumberPlaceHolder')}
                        countryCode={{
                          name: 'confirmMobileCountryCode',
                          error: !!formHook.formState.errors.confirmMobileCountryCode,
                          helperText: formHook.formState.errors?.confirmMobileCountryCode?.message?.toString(),
                        }}
                        countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                        number={{
                          name: 'confirmMobileContactNumber',
                          error: !!formHook.formState.errors.confirmMobileContactNumber,
                          helperText: formHook.formState.errors?.confirmMobileContactNumber?.message?.toString(),
                        }}
                        isPasteDisabled
                        isSkipTransform
                        isRequired
                        isDefaultToSingapore
                    />
                  </Box>
                </>
            )}
            {watchRadioButtonValue === 'N' && (
                <>
                  <Box className={classes.formElementContainer}>
                    <TextFieldForm
                        form={formHook}
                        id="inputText-candidateProfile-nric"
                        label={t('exam->>PayNowProxyDetails.NricLabel')}
                        error={!!formHook?.formState.errors.nricFin}
                        helperText={formHook?.formState.errors?.nricFin?.message?.toString()}
                        name="nricFin"
                        type="text"
                        isRequired
                        fullWidth
                        isPasteDisabled
                    />
                  </Box>
                  <Box className={classes.formElementContainer}>
                    <TextFieldForm
                        form={formHook}
                        id="inputText-candidateProfile-confirmNric"
                        label={t('exam->>PayNowProxyDetails.ConfirmNricLabel')}
                        error={!!formHook?.formState.errors.confirmNricFin}
                        helperText={formHook?.formState.errors?.confirmNricFin?.message?.toString()}
                        placeholder={t('exam->>PayNowProxyDetails.ConfirmNricPlaceHolder')}
                        name="confirmNricFin"
                        type="text"
                        isRequired
                        isPasteDisabled
                        fullWidth
                    />
                  </Box>
                </>
            )}
            <Box className={classes.buttonContainer}>
              <Button variant="text" type="button" onClick={() => closeModal()}>
                {t('admin->>UserGroups.Cancel')}
              </Button>
              <ApiButton
                  id="button-update-user-group-access-matrix"
                  type="submit"
                  status={apiButtonStatus}
              >
                {t('admin->>UserGroups.Confirm')}
              </ApiButton>
            </Box>
          </form>
        </DialogContent>
      </Dialog>)
  );
};
export default PayNowProxyDetailsModal;